import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoadWithInput } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import { usePendingUpdates } from "@redotech/react-util/use-pending-updates";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { redoCodeFormat } from "@redotech/redo-model/code";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
  IFulfillmentGroupData,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  FulfillmentGroupStatus,
  OpenLikeStatuses,
} from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
  ungroupFulfillmentOrderLineItems,
} from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { AttributesForNewFulfillmentGroups } from "@redotech/redo-model/fulfillments/split-fulfillment-group";
import {
  PickSessionStatus,
  PickSessionStrategy,
} from "@redotech/redo-model/wms/schema/pick-session";
import {
  alertOnFailure,
  hideNotification,
  showNotification,
  toast,
} from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { NotificationInstance } from "@redotech/redo-web/notification-provider";
import {
  ERROR_SOUND,
  SUCCESS_SOUND,
  SUCCESS_SOUND_2,
} from "@redotech/redo-web/sound/sounds";
import { Text } from "@redotech/redo-web/text";
import { Input } from "@redotech/ui/form";
import { ImmutableCountMap } from "@redotech/util/map";
import { ObjectId } from "bson";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PinVerificationProvider } from "../../common/pin-verification-context";
import { useRetryOnResolve } from "../../hooks/use-retry-on-resolve";
import { useWatchStream } from "../../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { isItemMatch } from "../../pick-sessions/util";
import { getOptimisticUpdates } from "../../utils/get-optimistic-updates";
import { getUserToOmsUser } from "../../utils/get-team-oms-users";
import { updateFulfillmentGroupsWithAlertFailure } from "../../utils/update-fulfillment-groups-with-retry";
import { ScanMode, scanModeStore } from "../common";
import { ScanningModals } from "../modals/modals";
import { AttributesForSplitShipmentModalData } from "../modals/partially-fulfill-modal";
import { initialScanningState, scanningStateReducer } from "./reducer";
import * as styles from "./styles.module.css";
import {
  Scanned,
  ScanningActionType,
  ScanningContextType,
  ScanningModal,
} from "./types";

const showScanNotification = (
  notificationInstance: NotificationInstance,
  timeout?: number,
) => {
  hideNotification();
  showNotification(notificationInstance);
  if (timeout) {
    setTimeout(() => {
      hideNotification();
    }, timeout);
  }
};

const getScannedNotificationInstance = (
  item: FulfillmentOrderLineItem,
  remaining: number,
): NotificationInstance => {
  return {
    title: `Scanned: ${item.productTitle}`,
    description: item.variantTitle,
    children: () => (
      <Flex>
        <Text color="secondary" fontSize="sm">
          {`${remaining} remaining`}
        </Text>
      </Flex>
    ),
    fullModalImage: item.imageUrl ?? undefined,
  };
};

const emptyContext: ScanningContextType = {
  handleScan: async () => {},
  handlePartialFulfill: () => {},
  scanned: ImmutableCountMap.empty(),
  scanMode: ScanMode.VERIFY_PRINT,
  pending: false,
  setScanned: () => {},
  setScanMode: () => {},
  setFulfillmentGroupData: () => {},
  reloadFulfillmentGroupData: () => {},
  updateFulfillmentGroups: () => {},
  updatesPending: false,
  fulfillmentGroupDataPending: false,
  handleActionButtonClick: () => {},
  setFulfillmentActionButton: () => {},
};

export const ScanningContext = createContext<ScanningContextType>(emptyContext);

export const ScanningContextProvider = memo(function ScanningContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const params = useParams();

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);

  const wmsEnabled = team.settings.outboundLabels.wms?.enabled;

  const value: ScanningContextType = { ...emptyContext };

  const [state, dispatch] = useReducer(
    scanningStateReducer,
    initialScanningState,
  );

  const {
    fulfillmentGroups,
    modalState,
    modalData,
    pickSession,
    relevantFulfillmentGroups,
    scanned,
  } = state;
  value.modalState = modalState;
  value.fulfillmentGroupData = state.fulfillmentGroupData;

  const fulfillmentGroupId = params.fulfillmentGroupId;

  /*
   * Scan mode
   *
   * While we handle scanMode in the local storage store, we're providing it
   * here to centralize the scanning state from the context of the consumer
   */
  let [scanMode, setScanMode] = useStore(scanModeStore);
  scanMode ??= ScanMode.VERIFY_PRINT;
  value.scanMode = scanMode;
  value.setScanMode = setScanMode;

  /*
   * Packages
   *
   * We load the packages here as they can be scanned to change
   */
  const packages = useLoad(async () => {
    if (!client) return [];

    const [customParcels, carrierParcels] = await Promise.all([
      client.getParcels(null),
      client.getCarrierParcels(null),
    ]);

    return [
      ...(customParcels?.parcels ?? []),
      ...(carrierParcels?.carrierParcels ?? []),
    ];
  }, [state.fulfillmentGroupData?._id]);

  // Fulfillment group data loading
  const [{ pending: fulfillmentGroupDataPending }, loadFulfillmentGroupData] =
    useTriggerLoadWithInput(
      async (
        {
          fulfillmentGroupId,
          requestSource,
          verifyAll,
          resetScanned,
        }: {
          requestSource?: "scan";
          fulfillmentGroupId?: string;
          verifyAll?: boolean;
          resetScanned?: boolean;
        },
        signal,
      ) => {
        try {
          if (!fulfillmentGroupId) {
            return null;
          }

          const { fulfillmentGroup, originalGroupWasMerged } =
            await client.getLatestOpenFulfillmentGroup(
              { fulfillmentGroupId, requestSource },
              { signal },
            );

          // Store originalGroupWasMerged for PIN verification check
          if (originalGroupWasMerged) {
            dispatch({
              type: ScanningActionType.OPEN_MODAL,
              payload: {
                type: ScanningModal.MERGED_PACKING_SLIP_SCANNED,
                data: fulfillmentGroup,
              },
            });
            return null;
          }

          if (fulfillmentGroup._id !== fulfillmentGroupId) {
            return handleNavigate(fulfillmentGroup._id);
          }

          const { assignedUser } = fulfillmentGroup;

          if (user && (!assignedUser || assignedUser._id === "unassigned")) {
            await updateFulfillmentGroupsWithAlertFailure(
              client,
              [{ fulfillmentGroupId, assignedUser: getUserToOmsUser(user) }],
              { teamId: team?._id },
            );
          }

          if (fulfillmentGroup.status === FulfillmentGroupStatus.Closed) {
            dispatch({
              type: ScanningActionType.OPEN_MODAL,
              payload: { type: ScanningModal.CLOSED_GROUP_WARNING },
            });
          }

          dispatch({
            type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
            payload: fulfillmentGroup,
          });

          if (verifyAll || (resetScanned && fulfillmentGroup.scanVerified)) {
            const lineItems = groupFulfillmentOrderLineItems(
              fulfillmentGroup.items,
            );
            setScanned(
              ImmutableCountMap.fromEntries(
                lineItems.map((item) => [item.externalId, item.quantity]),
              ),
              fulfillmentGroup,
            );
          } else if (resetScanned) {
            setScanned(ImmutableCountMap.empty(), fulfillmentGroup);
          }

          return fulfillmentGroup;
        } catch (error) {
          datadogLogs.logger.error("Error accessing scan page", { error });
          if ((error as any).status === 401) {
            toast("Access denied", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 5 }),
            });
          } else if ((error as any).message?.toLowerCase().includes("abort")) {
            datadogLogs.logger.error("AbortError", { error });
          } else {
            toast("Redo encountered an error, please try again", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 5 }),
            });
            navigate("../scan");
          }

          return null;
        }
      },
    );
  const reloadFulfillmentGroupData = useHandler(() => {
    loadFulfillmentGroupData({ fulfillmentGroupId });
  });
  value.reloadFulfillmentGroupData = reloadFulfillmentGroupData;
  value.fulfillmentGroupDataPending = fulfillmentGroupDataPending;
  const setFulfillmentGroupData = useHandler(
    (data: FulfillmentGroupData | null) => {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
        payload: data,
      });
    },
  );
  value.setFulfillmentGroupData = setFulfillmentGroupData;

  const updateFulfillmentGroupData = useHandler(
    (fulfillmentGroupUpdates: FulfillmentGroupUpdate[]) => {
      if (!state.fulfillmentGroupData) {
        return;
      }
      const { newData, updates } = getOptimisticUpdates(
        [state.fulfillmentGroupData],
        fulfillmentGroupUpdates,
      );
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
        payload: newData[0],
      });
      addPendingUpdates(updates);
    },
  );
  value.updateFulfillmentGroups = updateFulfillmentGroupData;

  useEffect(() => {
    if (
      fulfillmentGroupId &&
      !state.fulfillmentGroupIds.includes(fulfillmentGroupId)
    ) {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP,
        payload: fulfillmentGroupId,
      });

      loadFulfillmentGroupData({ fulfillmentGroupId });
    }
  }, [fulfillmentGroupId, loadFulfillmentGroupData, state.fulfillmentGroupIds]);

  const pendingFulfillmentGroups = useWatchStream(
    client,
    "watchFulfillmentGroups",
    { fulfillmentGroupIds: state.fulfillmentGroupIds },
    ({ fulfillmentGroups }) => {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUPS,
        payload: fulfillmentGroups,
      });
    },
  );

  const allFulfillmentGroupsLoaded = state.fulfillmentGroupIds.every(
    (id) => state.fulfillmentGroups[id],
  );

  /*
   * Fulfillment action button
   *
   * The fulfillment action button is the primary action button on the
   * fulfillment group page. We're using it to print labels once we've
   * fulfilled all items when enabled.
   */
  const [fulfillmentActionButton, setFulfillmentActionButton] =
    useState<HTMLButtonElement | null>(null);
  value.setFulfillmentActionButton = setFulfillmentActionButton;

  // fulfillmentActionButton may be disabled when we first load the page,
  // wait for it to be enabled before we try to click it
  const [, handleActionButtonClick] = useRetryOnResolve(
    fulfillmentGroupDataPending,
    async () => {
      // We use fulfillmentActionButton.click() to get the validation and action
      // selection logic provided by the button itself
      fulfillmentActionButton?.click();
    },
  );
  value.handleActionButtonClick = handleActionButtonClick;

  /*
   * Handle navigation
   *
   * Unifies navigation from within the scan routes, if we're scanning
   * to a new  group, kick off a reload before navigation occurs
   */
  const handleNavigate = useHandler(
    async (
      value: string,
      {
        resetScanned,
        verifyScanned,
      }: { resetScanned?: boolean; verifyScanned?: boolean } = {
        resetScanned: true,
        verifyScanned: false,
      },
    ) => {
      if (ObjectId.isValid(value)) {
        dispatch({
          type: ScanningActionType.SET_FULFILLMENT_GROUP,
          payload: value,
        });

        loadFulfillmentGroupData({
          requestSource: "scan",
          fulfillmentGroupId: value,
          resetScanned,
        });
      }

      if (verifyScanned) {
        /*
         * Handle navigation if verified
         *
         * If we're in verify print mode, and we haven't scanned all items, we verify
         * with the user before navigating
         */

        if (!state.fulfillmentGroupData) {
          console.error("No fulfillment group data, required for verification");
          return;
        }

        const allScanned =
          scanned.sum() === state.fulfillmentGroupData.totalItemQuantity;
        if (!allScanned && scanMode === ScanMode.VERIFY_PRINT) {
          dispatch({
            type: ScanningActionType.OPEN_MODAL,
            payload: {
              type: ScanningModal.INCOMPLETE_VERIFICATION,
              data: value,
            },
          });
          return;
        }
      }

      const pathPrefix = fulfillmentGroupId ? "../" : "";
      const path = `${pathPrefix}${value}`;
      navigate(path, { relative: "path" });
    },
  );

  /*
   * Handle fulfillment group selection
   *
   * Unifies fulfillment group selection from within the scan routes
   */
  const handleSelectFulfillmentGroup = useHandler(
    async ({ _id: fulfillmentGroupId, assignedUserId }: FulfillmentGroup) => {
      if (user && (!assignedUserId || assignedUserId === "unassigned")) {
        try {
          await updateFulfillmentGroupsWithAlertFailure(
            client,
            [{ fulfillmentGroupId, assignedUser: getUserToOmsUser(user) }],
            { teamId: team?._id },
          );
        } catch (error) {
          console.error(
            "Error updating fulfillment group from scan start page",
            error,
          );
          throw error;
        }
      }

      await handleNavigate(fulfillmentGroupId);
    },
  );

  // Update groups with optimistic updates and pending updates tracking
  const updateGroups = useHandler(async (updates: FulfillmentGroupUpdate[]) => {
    try {
      const updatedGroups = await updateFulfillmentGroupsWithAlertFailure(
        client,
        updates,
        { teamId: team?._id },
      );
      const updatedGroupsArray = Object.values(updatedGroups);
      if (updatedGroupsArray.length > 0) {
        dispatch({
          type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
          payload: updatedGroupsArray[0],
        });
      }
    } catch (error) {
      console.error("Error updating fulfillment groups from scan page", error);
      throw error;
    }
  });

  const { updatesPending, addPendingUpdates } = usePendingUpdates({
    updateGroups,
    identifier: (update) => update.fulfillmentGroupId,
  });
  value.updatesPending = updatesPending;

  const handleSelectPickSessionItem = useHandler((value: string): boolean => {
    if (!pickSession) {
      return false;
    }

    const item = pickSession?.items.find((item) => {
      if (
        item.fulfillmentGroupId === state.fulfillmentGroupData?._id &&
        !OpenLikeStatuses.includes(state.fulfillmentGroupData?.status)
      ) {
        return false;
      }

      return (
        isItemMatch(item, value, team) &&
        OpenLikeStatuses.includes(
          state.fulfillmentGroups[item.fulfillmentGroupId].status,
        )
      );
    });

    if (!item || !item.externalId) {
      if (
        pickSession?.items.find(
          (item) =>
            isItemMatch(item, value, team) &&
            OpenLikeStatuses.includes(
              state.fulfillmentGroups[item.fulfillmentGroupId].status,
            ),
        )
      ) {
        toast("All tote items have been fulfilled", { variant: "error" });
        ERROR_SOUND.currentTime = 0;
        void ERROR_SOUND.play();
        throw new Error("All tote items have been fulfilled");
      }

      return false;
    }

    const newScanned = ImmutableCountMap.empty<string, number>();
    setScanned(
      newScanned.increment(item.externalId, 1),
      state.fulfillmentGroups[item.fulfillmentGroupId],
    );
    void handleNavigate(item.fulfillmentGroupId, {
      resetScanned: false,
      verifyScanned: !!fulfillmentGroupId,
    });
    return true;
  });

  /*
   * =======================================================================
   * SCANNING LOGIC
   * =======================================================================
   *
   * The following handlers are responsible for handling the actual scanning
   * logic for the different workflows below the scan route
   *
   * - Tote item selection modal
   * - Start page
   * - Fulfillment group page
   */

  /*
   * Handle tote order selection
   *
   * After a tote is scanned, we need to handle an item scan specifically pulling
   * the item from the active pick session
   */
  const handleToteOrderSelectionScan = useHandler(async (value?: string) => {
    if (!pickSession || !value) {
      // If we don't have a pick session, we can't select an item
      return;
    }

    // If we have a pick session, we need to fin
    try {
      const item = handleSelectPickSessionItem(value);

      if (item) {
        dispatch({ type: ScanningActionType.CLOSE_MODAL });
      } else {
        toast("Item not found in tote", { variant: "error" });
        ERROR_SOUND.currentTime = 0;
        void ERROR_SOUND.play();
      }
    } catch (error) {
      // If we throw, we don't have an item to select
      return;
    }
  });
  /*
   * if we scan an item too quickly after scanning a tote,
   * we may try to submit before we've loaded the relevant
   * fulfillment groups, set let's try once we have them
   */
  const [{ pending: toteOrderSelectionPending }, handleToteOrderSelection] =
    useRetryOnResolve(
      pendingFulfillmentGroups || !allFulfillmentGroupsLoaded,
      handleToteOrderSelectionScan,
    );

  /*
   * Handle tote or fulfillment group scanning
   *
   * Any time we are scanning in a new order, we could also be scanning in a tote.
   */
  const handleToteOrFulfillmentGroupScan = useHandler(async (value: string) => {
    try {
      /*
       * Handle tote scanning
       *
       * If a tote is scanned, we pull the fulfillment group id from the tote
       */
      let fulfillmentGroupId = value;

      if (wmsEnabled) {
        try {
          const { pickSession, tote } =
            await client.getPickSessionByToteBarcode({ barcode: value });

          /*
           * If we didn't throw, but we didn't find a pick session,
           * the tote isn't in an active pick session
           */
          if (!tote || !pickSession) {
            toast("Tote not in an active pick session", { variant: "error" });
            return;
          }

          /*
           * If the pick session is not in a picked or printed state, we can't verify
           * the tote orders
           */
          if (
            pickSession.status !== PickSessionStatus.PICKED &&
            pickSession.status !== PickSessionStatus.PRINTED
          ) {
            toast("Tote's pick session is not in a picked or printed state", {
              variant: "error",
            });
            return;
          }

          /*
           * If the pick session is a single item pick session, we navigate
           * now work within the context of the pick session, each item scan should load
           * a fulfillment group that contains the item, as well as set it as scanned
           */
          if (pickSession.strategy === PickSessionStrategy.SINGLE_ITEM) {
            dispatch({
              type: ScanningActionType.SET_PICK_SESSION,
              payload: pickSession,
            });

            return;
          }

          /*
           * If the pick session is a multi-item pick session, we need to find the item's
           * fulfillment group. we currently assume that the tote will contain a single
           * fulfillment group, and that a fulfillment group won't span multiple totes,
           * so we handle as though we were scanning a fulfillment group id
           */
          const toteId = tote._id;
          const toteItem = pickSession.items.find(
            (item) =>
              item.quantityByTote &&
              Object.keys(item.quantityByTote).includes(toteId),
          );

          if (!toteItem) {
            // If we get here, we have an issue, the pick session we pull from the tote
            // SHOULD be associated with at least one item in the tote
            toast("No items found in valid tote", { variant: "error" });
            return;
          }

          ({ fulfillmentGroupId } = toteItem);
        } catch (error) {
          // If we throw here, we're not scanning a tote
        }
      }

      /*
       * Handle fulfillment group navigation
       *
       * whether we scanned a tote or a fulfillment group id, we now need to load the
       * fulfillment group
       */
      const { fulfillmentGroups } = await client.getRelevantFulfillmentGroups({
        identifier: fulfillmentGroupId,
      });

      /*
       * If we don't find a valid scan from the fulfillment page, we throw a toast or open
       * a modal, AND play a sound.
       */
      if (fulfillmentGroups.length === 0) {
        console.error("No match found for scanned barcode", {
          scannedValue: value,
          groupId: state.fulfillmentGroupData?._id,
          groupLineItems: state.fulfillmentGroupData?.items.map((li) => ({
            barcode: li.barcode,
            sku: li.sku,
            productTitle: li.productTitle,
          })),
        });

        if (params.fulfillmentGroupId) {
          dispatch({
            type: ScanningActionType.OPEN_MODAL,
            payload: { type: ScanningModal.WRONG_BARCODE, data: value },
          });
        } else {
          toast("No shipments found", { variant: "error" });
        }

        ERROR_SOUND.currentTime = 0;
        void ERROR_SOUND.play();
        return;
      }

      /*
       * If we have multiple fulfillment groups, we need to select one
       */
      if (fulfillmentGroups.length > 1) {
        dispatch({
          type: ScanningActionType.SELECT_FULFILLMENT_GROUP,
          payload: fulfillmentGroups,
        });
      } else {
        await handleSelectFulfillmentGroup(fulfillmentGroups[0]);
      }
    } catch (err) {
      if (err instanceof Error && err.message === "Pick session found") {
        return;
      }
      console.error("Error accessing scan page", err);
      if ((err as any).statusCode === 401) {
        toast("Access denied", { variant: "error" });
        return;
      }
    }
  });

  /*
   * Handle fulfillment page scanning
   *
   * From the fulfillment page we allow the scanning of:
   *  - Items to verify items
   *  - Items on other fulfillment groups in a pick session to navigate to them
   *  - Package/parcel hashed names to update selected parcels
   *  - Order numbers
   */
  const [{ pending: fulfillmentPageScanPending }, handleFulfillmentPageScan] =
    useTriggerLoadWithInput(async (value: string) => {
      if (!state.fulfillmentGroupData) {
        return;
      }

      /*
       * Handle package/parcel hashed names
       */
      if (
        packages.value?.some(
          (parcel) => parcel.hashedName?.toLowerCase() === value.toLowerCase(),
        ) &&
        state.fulfillmentGroupData.status !== FulfillmentGroupStatus.Closed
      ) {
        const scannedParcel = packages.value?.find(
          (parcel) => parcel.hashedName?.toLowerCase() === value.toLowerCase(),
        );
        if (scannedParcel) {
          datadogLogs.logger.info("Package shortcut scanned", {
            teamId: team?._id,
            userId: user?._id,
            fulfillmentGroupId: state.fulfillmentGroupData._id,
            orderId: state.fulfillmentGroupData._id,
            packageName: scannedParcel.name,
            packageHashedName: scannedParcel.hashedName,
            scanValue: value,
            source: "fulfillment-app",
            shortcutType: "scan",
            action: "Select package/parcel",
          });

          await updateFulfillmentGroupsWithAlertFailure(
            client,
            [
              {
                fulfillmentGroupId: state.fulfillmentGroupData._id,
                selectedParcels: [scannedParcel],
              },
            ],
            { teamId: team?._id },
          );
        } else {
          toast("Parcel not found", { variant: "error" });
        }

        return;
      }

      /*
       * Handle navigating to other fulfillment groups in a pick session
       *
       * We're running this before checking for items in the fulfilment group
       * to avoid over incrementing quantities for another fulfillment group
       * that contains the same item
       *
       * should only actually run when we have a pick session
       */
      if (wmsEnabled && state.pickSession) {
        try {
          const found = handleSelectPickSessionItem(value);
          if (found) {
            return;
          }
        } catch (error) {
          // If we throw, we don't have an item to select
          return;
        }
      }

      /*
       * Handle verifying items
       */
      const matchedItem = groupFulfillmentOrderLineItems(
        state.fulfillmentGroupData.items,
      ).find(
        (item) =>
          isItemMatch(item, value, team) &&
          scanned.get(item.externalId) < item.quantity,
      );
      if (matchedItem) {
        const currentScannedValue = scanned.get(matchedItem.externalId) + 1;
        const currentScannedSum = scanned.sum() + 1;
        setScanned(scanned.increment(matchedItem.externalId));

        // Log item scan
        datadogLogs.logger.info("Item scanned for verification", {
          teamId: team?._id,
          userId: user?._id,
          fulfillmentGroupId: state.fulfillmentGroupData._id,
          orderId: state.fulfillmentGroupData._id,
          itemExternalId: matchedItem.externalId,
          itemName: matchedItem.productTitle,
          itemSku: matchedItem.sku,
          scannedCount: currentScannedValue,
          totalRequired: matchedItem.quantity,
          allItemsScanned:
            currentScannedSum === state.fulfillmentGroupData.totalItemQuantity,
          scanValue: value,
          source: "fulfillment-app",
        });

        const showNotifications =
          team?.settings.outboundLabels.showScanNotifications ?? true;
        if (showNotifications) {
          showScanNotification(
            getScannedNotificationInstance(
              matchedItem,
              matchedItem.quantity - currentScannedValue,
            ),
            5000,
          );
        }

        if (
          currentScannedSum === state.fulfillmentGroupData.totalItemQuantity
        ) {
          SUCCESS_SOUND_2.currentTime = 0;
          void SUCCESS_SOUND_2.play();

          if (team?.settings.outboundLabels.printLabelOnAllScanVerified) {
            handleActionButtonClick();
          }
        } else {
          SUCCESS_SOUND.currentTime = 0;
          void SUCCESS_SOUND.play();
        }

        return;
      }

      /*
       * Handle fulfillment group navigation
       *
       * We assume that if the scanned value is a valid object id, it's a
       * fulfillment group id.
       */
      if (ObjectId.isValid(value)) {
        await handleNavigate(value, { verifyScanned: true });
        return;
      }

      try {
        const code = redoCodeFormat.read(value.trim());
        if (code.type === "FF") {
          value = code.id.toString();
        }
      } catch {
        //
      }

      await handleToteOrFulfillmentGroupScan(value);
    });

  /*
   * Handle start page scanning
   *
   * From the start page we allow the scanning of:
   *  - Fulfillment group ids
   *  - Tote ids
   */
  const [{ pending: startPageScanPending }, handleStartPageScan] =
    useTriggerLoadWithInput(async (value: string) => {
      /*
       * Handle fulfillment group navigation
       *
       * We assume that if the scanned value is a valid object id, it's a
       * fulfillment group id.
       */
      if (ObjectId.isValid(value)) {
        datadogLogs.logger.info("Fulfillment group navigation scanned", {
          teamId: team?._id,
          userId: user?._id,
          targetFulfillmentGroupId: value,
          scanValue: value,
          source: "fulfillment-app",
          action: "Navigate to fulfillment group",
        });
        await handleNavigate(value);
        return;
      }

      try {
        const code = redoCodeFormat.read(value.trim());
        if (code.type === "FF") {
          value = code.id.toString();
        }
      } catch {
        //
      }

      await handleToteOrFulfillmentGroupScan(value);
    });

  /*
   * Handle partial fulfill
   *
   * If the user requests to partially fulfill, we verify that request in a modal,
   * As we don't have an asynchronous confirmation modal flow, we handle the actually
   * partial fulfillment request in the modal
   */
  const handlePartialFulfill = useHandler(() => {
    dispatch({
      type: ScanningActionType.OPEN_MODAL,
      payload: { type: ScanningModal.PARTIAL_FULFILL },
    });
  });
  value.handlePartialFulfill = handlePartialFulfill;

  /*
   * Do partial fulfillment
   *
   * This function is called from the verification modal
   */
  const [{ pending: partialFulfillPending }, doPartialFulfill] =
    useTriggerLoadWithInput(
      (partiallyFulfillModalData: AttributesForSplitShipmentModalData) =>
        alertOnFailure("Failed to partially fulfill order")(async () => {
          if (!fulfillmentGroupId) {
            return;
          }
          dispatch({ type: ScanningActionType.CLOSE_MODAL });
          const originalLineItems = state.fulfillmentGroupData!.items;
          const groupedLineItems = groupFulfillmentOrderLineItems(
            originalLineItems,
            { usePrice: true },
          );

          const groupedItemsToSplit = groupedLineItems.map((item) => {
            const count = scanned.get(item.externalId);
            return { ...item, quantity: item.quantity - count };
          });

          const ungrouped = ungroupFulfillmentOrderLineItems(
            originalLineItems,
            groupedItemsToSplit,
          );

          const itemsToSplit = ungrouped.map((item) => ({
            fulfillmentOrderLineItemId: item.externalId,
            quantity: item.quantity,
          }));

          const attributesForNewFulfillmentGroups: AttributesForNewFulfillmentGroups =
            {
              internalNote: partiallyFulfillModalData.internalNote,
              status: partiallyFulfillModalData.newStatus,
              holdDetails:
                partiallyFulfillModalData.newStatus ===
                FulfillmentGroupStatus.OnHold
                  ? {
                      onHoldUntil: partiallyFulfillModalData.onHoldUntil,
                      reason: partiallyFulfillModalData.onHoldReason,
                    }
                  : undefined,
            };

          const { success, warningMessage, newGroupIds } =
            await client.splitFulfillmentGroup({
              originalGroupId: fulfillmentGroupId,
              itemsToSplit,
              attributesForNewFulfillmentGroups,
            });

          if (!success && warningMessage) {
            toast(warningMessage, { variant: "warning" });
            return;
          }

          // Show toast with link to new shipments created from split
          if (newGroupIds) {
            let newGroupIndex = 0;
            for (const groupId of newGroupIds) {
              newGroupIndex++;
              toast(
                <Flex align="center" justify="space-between" w="full">
                  <Text>
                    New shipment {newGroupIndex} created from partial fulfill.
                  </Text>
                  <RedoButton
                    hierarchy="primary"
                    IconTrailing={ExternalLinkIcon}
                    onClick={() => {
                      window.open(
                        `/stores/${team._id}/order-management/outbound-labels/scan/${groupId}`,
                        "_blank",
                      );
                    }}
                    size="xs"
                    text="Go to new shipment"
                  />
                </Flex>,
                { variant: "success", persist: true },
              );
            }
          }

          loadFulfillmentGroupData({ fulfillmentGroupId, verifyAll: true });
        }),
    );
  value.partialFulfillPending = partialFulfillPending;

  /*
   * Pick session progress bar
   *
   * If we are working within the context of a pick session, we want to display a progress bar
   * that shows the user how far along they are in the pick session.
   */
  if (pickSession && Object.keys(fulfillmentGroups).length > 0) {
    const fulfilling = Object.values(fulfillmentGroups).length;
    const fulfilled =
      Object.values(fulfillmentGroups).reduce(
        (acc, { status }) => acc + (OpenLikeStatuses.includes(status) ? 0 : 1),
        0,
      ) + 1;

    const percentage = Math.min(
      100,
      Math.max(0, (fulfilled / fulfilling) * 100),
    );

    value.progress = (
      <Flex dir="row" gap="md">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          Pick session order {fulfilled} of {fulfilling}:
        </Text>
        <Flex className={styles.progressBar} dir="column" justify="center">
          <RedoProgressBar
            fillColor="var(--redo-colors-foreground-fg-success-primary)"
            percentage={percentage}
            style="thin"
          />
        </Flex>
      </Flex>
    );
  }

  /*
   * Scanned items
   *
   * We keep track of the scanned items in a central place and handle the
   * verification once all items are scanned
   */
  const setScanned = useHandler(
    (newScanned: Scanned, scanningFulfillmentGroup?: IFulfillmentGroupData) => {
      dispatch({ type: ScanningActionType.SET_SCANNED, payload: newScanned });

      const fulfillmentGroupData =
        scanningFulfillmentGroup ?? state.fulfillmentGroupData;

      const totalItems = fulfillmentGroupData?.totalItemQuantity;
      const currentlyVerified = !!fulfillmentGroupData?.scanVerified;

      if (totalItems !== undefined && user && fulfillmentGroupData?._id) {
        const allItemsScanned = newScanned.sum() === totalItems;
        if (allItemsScanned !== currentlyVerified) {
          updateFulfillmentGroupData([
            {
              fulfillmentGroupId: fulfillmentGroupData._id,
              scanVerified: allItemsScanned
                ? {
                    verifiedBy: {
                      _id: user._id,
                      email: user.email,
                      createdAt: new Date(user.createdAt),
                      firstName: user.firstName,
                      lastName: user.lastName,
                      name: user.name,
                      updatedAt: new Date(user.updatedAt),
                      team: user.team,
                    },
                    verifiedAt: new Date(),
                  }
                : null,
            },
          ]);
        }
      }
    },
  );
  value.scanned = scanned;
  value.setScanned = setScanned;

  /*
   * Herein lies the crux of the state structure.
   *
   * We need to auto scan on load of an order by item scan when within
   * the context of a pick session, so we don't want to clear or even
   * necessarily set the scanned items on load if we are coming from a scan
   * in, however, if we refresh, we want to set the scanned items to match
   * the fulfillment group data.
   *
   * We're deriving reload as entering this page with an empty scanned state,
   * if we navigate through scan, we should set the scanned state to match
   * the fulfillment group data before navigation. If the scanned state SHOULD
   * be empty, and this isn't a reload, we don't need to do anything. This
   * will duplicate some work, but it's not a big deal.
   *
   * When we are resetting the scanned state, we DON'T want to update any
   * verification data, so we pass in an empty object as the fulfillment group
   * data.
   */
  useEffect(() => {
    if (!fulfillmentGroupId) {
      setScanned(ImmutableCountMap.empty(), {} as IFulfillmentGroupData);
    }
  }, [fulfillmentGroupId, setScanned]);
  useEffect(() => {
    const handlePopState = () => {
      // Handling dispatch directly here to keep unsetting verified
      dispatch({
        type: ScanningActionType.SET_SCANNED,
        payload: ImmutableCountMap.empty(),
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (
      state.fulfillmentGroupData?._id &&
      scanned.sum() === 0 &&
      state.fulfillmentGroupData?.scanVerified
    ) {
      const lineItems = groupFulfillmentOrderLineItems(
        state.fulfillmentGroupData.items,
      );
      setScanned(
        ImmutableCountMap.fromEntries(
          lineItems.map((item) => [item.externalId, item.quantity]),
        ),
      );
    }
    /*
     * `setScanned` should be consistent across renders, and we only care
     * about the state of `scanned` on change of `state.fulfillmentGroupData._id`
     * so we shouldn't include `setScanned` or `scanned` in the deps array.
     */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.fulfillmentGroupData?._id]);

  /*
   * Scan
   *
   * This is the main scan function. It handles the different states of the app and calls the
   * appropriate scan function.
   */
  const handleScan = useHandler((barcode: Input<string>) => {
    hideNotification();

    const value = barcode.value.trim();
    barcode.setValue("");

    if (modalState === ScanningModal.TOTE_ORDER_SELECTION) {
      handleToteOrderSelection(value);
    } else if (fulfillmentGroupId) {
      handleFulfillmentPageScan(value);
    } else {
      handleStartPageScan(value);
    }
  });
  value.handleScan = handleScan;

  value.pending =
    startPageScanPending ||
    fulfillmentPageScanPending ||
    toteOrderSelectionPending;

  return (
    <PinVerificationProvider>
      <ScanningContext.Provider value={value as ScanningContextType}>
        {children}
        <ScanningModals
          doPartialFulfill={doPartialFulfill}
          handleNavigate={handleNavigate}
          handleSelectFulfillmentGroup={handleSelectFulfillmentGroup}
          onClose={() => dispatch({ type: ScanningActionType.CLOSE_MODAL })}
          openModal={modalState}
          openModalData={modalData}
          relevantFulfillmentGroups={relevantFulfillmentGroups}
        />
      </ScanningContext.Provider>
    </PinVerificationProvider>
  );
});
